import type { JsonScalar, MaskStrategy } from "../contracts/types";
import { createSeededRandom, stableHash32 } from "./ids";

/**
 * Contexto compartido de una ejecución de enmascarado. `consistencyMap`
 * garantiza P13-R3 ("mantener... relaciones configuradas"): con la estrategia
 * "hash", el MISMO valor original en el MISMO campo produce siempre el MISMO
 * valor enmascarado dentro de la ejecución — así se conserva la relación
 * referencial (p. ej. un mismo `customer_id` repetido en varias filas sigue
 * siendo identificable como "la misma entidad" tras el enmascarado, sin
 * exponer el valor real).
 */
export interface MaskContext {
  runSeed: string;
  consistencyMap: Map<string, JsonScalar>;
}

export function createMaskContext(runSeed: string): MaskContext {
  return { runSeed, consistencyMap: new Map() };
}

const NAME_SYLLABLES = ["ka", "lo", "mi", "ren", "so", "tu", "va", "ni", "bel", "dor"];

function syntheticToken(seed: number, length: number): string {
  const random = createSeededRandom(seed);
  let token = "";
  while (token.length < length) {
    token += NAME_SYLLABLES[Math.floor(random() * NAME_SYLLABLES.length)];
  }
  return token.slice(0, length);
}

function typedHash(value: JsonScalar, seedKey: string): JsonScalar {
  if (value === null) return null;
  const seed = stableHash32(seedKey);
  if (typeof value === "number") {
    const magnitude = Math.max(1, Math.abs(value));
    const digits = Math.max(1, Math.floor(Math.log10(magnitude)) + 1);
    const range = 10 ** digits;
    return (seed % range) * (value < 0 ? -1 : 1);
  }
  if (typeof value === "boolean") {
    return seed % 2 === 0;
  }
  // string: hash hexadecimal de longitud fija, nunca reversible con las herramientas de esta app.
  return `masked_${seed.toString(16).padStart(8, "0")}`;
}

function typedRandom(value: JsonScalar, seedKey: string): JsonScalar {
  if (value === null) return null;
  const seed = stableHash32(seedKey);
  const random = createSeededRandom(seed);
  if (typeof value === "number") {
    const magnitude = Math.max(1, Math.abs(value));
    const digits = Math.max(1, Math.floor(Math.log10(magnitude)) + 1);
    const range = 10 ** digits;
    return Math.floor(random() * range) * (value < 0 ? -1 : 1);
  }
  if (typeof value === "boolean") {
    return random() >= 0.5;
  }
  if (value.includes("@")) {
    return `${syntheticToken(seed, 8)}@example.test`;
  }
  return syntheticToken(seed, Math.min(Math.max(value.length, 4), 16));
}

function generalizeValue(value: JsonScalar, param: string | undefined): JsonScalar {
  if (value === null) return null;
  if (typeof value === "number") {
    const bucket = param ? Number(param) : 10;
    const safeBucket = Number.isFinite(bucket) && bucket > 0 ? bucket : 10;
    return Math.floor(value / safeBucket) * safeBucket;
  }
  if (typeof value === "boolean") return value; // sin generalización útil para booleanos
  const isoDate = /^\d{4}-\d{2}-\d{2}/.exec(value);
  if (isoDate) {
    const granularity = param === "year" ? 4 : 7; // "YYYY" o "YYYY-MM"
    return value.slice(0, granularity);
  }
  const keepChars = param ? Number(param) : 1;
  const safeKeep = Number.isFinite(keepChars) && keepChars >= 0 ? keepChars : 1;
  if (value.length <= safeKeep) return value;
  return `${value.slice(0, safeKeep)}${"*".repeat(Math.min(value.length - safeKeep, 8))}`;
}

function redactValueTyped(value: JsonScalar): JsonScalar {
  if (value === null) return null;
  if (typeof value === "number") return 0;
  if (typeof value === "boolean") return false;
  return "[REDACTED]";
}

/**
 * Aplica una estrategia de máscara a un valor de celda, preservando siempre
 * el tipo original (P13-R3). `rowIndex` participa en la semilla de
 * "randomize" para que el mismo valor pueda mostrarse de forma distinta en
 * cada fila (a diferencia de "hash", que es intencionalmente consistente).
 */
export function applyMaskValue(
  value: JsonScalar,
  field: string,
  strategy: MaskStrategy,
  param: string | undefined,
  rowIndex: number,
  ctx: MaskContext,
): JsonScalar {
  if (strategy === "preserve") return value;
  if (value === null) return null;

  if (strategy === "redact") return redactValueTyped(value);
  if (strategy === "generalize") return generalizeValue(value, param);

  if (strategy === "hash") {
    const key = `${ctx.runSeed}::hash::${field}::${String(value)}`;
    const cached = ctx.consistencyMap.get(key);
    if (cached !== undefined) return cached;
    const masked = typedHash(value, key);
    ctx.consistencyMap.set(key, masked);
    return masked;
  }

  // randomize: deliberadamente NO usa el mapa de consistencia, para que la
  // UI y el informe de cobertura puedan mostrar la diferencia con "hash".
  const key = `${ctx.runSeed}::randomize::${field}::row${rowIndex}::${String(value)}`;
  return typedRandom(value, key);
}
